import * as THREE from "three";
import { CONFIG, type QualityPreset } from "../config";
import { Input } from "../engine/input";
import { AudioSys } from "../engine/audio";
import { createNightEnv, createRenderer } from "../engine/renderer";
import { hitscan } from "../engine/collision";
import { buildTextures } from "../world/textures";
import { buildMaterials } from "../world/materials";
import { LightRig } from "../world/lighting";
import { buildLevel } from "../world/level";
import { Weather } from "../world/weather";
import { createSky, makeFlare } from "../world/sky";
import { Player } from "../player/player";
import { Arsenal } from "../combat/weapons";
import { FX } from "../combat/fx";
import { Director } from "../ai/enemy";
import { HUD } from "../ui/hud";
import { Mission } from "./mission";

type Mode = "menu" | "playing" | "paused" | "dead" | "victory";

export class App {
  private root: HTMLElement;
  private canvas: HTMLCanvasElement;
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private input: Input;
  private audio = new AudioSys();
  private stack!: ReturnType<typeof createRenderer>;
  private player!: Player;
  private arsenal!: Arsenal;
  private fx!: FX;
  private lights!: LightRig;
  private weather!: Weather;
  private director!: Director;
  private hud!: HUD;
  private mission!: Mission;
  private level!: ReturnType<typeof buildLevel>;
  private mode: Mode = "menu";
  private quality: QualityPreset = "high";
  private clock = new THREE.Clock();
  private attract = 0;
  private raf = 0;
  private grenades: { pos: THREE.Vector3; vel: THREE.Vector3; life: number; mesh: THREE.Mesh }[] = [];
  private nadeGeo = new THREE.SphereGeometry(0.08, 10, 8);
  private nadeMat = new THREE.MeshStandardMaterial({ color: 0x2a2e24, roughness: 0.5, metalness: 0.4 });
  private nadeCd = 0;
  private wallRay = new THREE.Raycaster();
  private warnedContact = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.canvas = root.querySelector("#c")!;
    this.camera = new THREE.PerspectiveCamera(CONFIG.worldFov, 1, 0.05, 280);
    this.input = new Input(this.canvas);
    this.scene.fog = new THREE.FogExp2(0x121822, 0.011);
    this.scene.background = new THREE.Color(0x0c121c);
    this.bindUI();
  }

  async start() {
    this.stack = createRenderer(this.canvas, this.scene, this.camera);
    const env = createNightEnv(this.stack.renderer);
    this.scene.environment = env;
    const tex = await buildTextures();
    const mats = buildMaterials(tex, env);
    this.lights = new LightRig(this.scene, this.quality);
    this.level = buildLevel(mats, this.lights);
    this.scene.add(this.level.group);
    this.scene.add(createSky());
    for (const lamp of this.lights.lamps) {
      const flare = makeFlare(lamp.color.getHex(), lamp.kind === "red" ? 1.1 : 2.2);
      flare.position.copy(lamp.pos);
      this.scene.add(flare);
    }
    this.weather = new Weather(this.scene, tex, this.quality);
    this.player = new Player(this.camera);
    this.scene.add(this.camera);
    this.player.spawn(this.level.playerStart, Math.PI);
    this.arsenal = new Arsenal(mats, tex, this.camera);
    this.fx = new FX(this.scene, tex, mats, this.quality);
    this.director = new Director(this.level, mats);
    this.scene.add(this.director.group);
    this.hud = new HUD(this.root);
    this.mission = new Mission(this.level);
    this.camera.add(new THREE.AudioListener());
    this.stack.resize(window.innerWidth, window.innerHeight);
    window.addEventListener("resize", () => this.stack.resize(window.innerWidth, window.innerHeight));
    window.addEventListener("contextmenu", (e) => e.preventDefault());
    const shot = new URLSearchParams(location.search).get("shot");
    if (shot) {
      this.root.querySelector("#menu")!.classList.add("hidden");
      const hint = this.root.querySelector<HTMLElement>("#hint");
      if (hint) hint.style.display = "none";
      this.hud.show(true);
      this.mode = "playing";
      document.body.classList.add("cinematic");
      this.mission.start();
      this.director.frozen = true;
      (window as unknown as { __halyard: App }).__halyard = this;
      const aimSpawn = (x: number, y: number, z: number, tx: number, ty: number, tz: number) => {
        const dx = tx - x;
        const dz = tz - z;
        const yaw = Math.atan2(-dx, -dz);
        const dist = Math.hypot(dx, ty - y, dz) || 1;
        this.player.spawn(new THREE.Vector3(x, y, z), yaw);
        this.player.pitch = -0.06;
      };
      if (shot === "apron") aimSpawn(5, 0, 11, 0, 1.3, 28);
      else if (shot === "yard") aimSpawn(-2, 0, 31, 10, 1.2, 42);
      else if (shot === "plant") aimSpawn(0, 0, 56, 0, 1.7, 72);
      else if (shot === "pad") aimSpawn(3, 8.22, 86, 0, 8.7, 93);
      else if (shot === "gun") aimSpawn(4, 0, 20, 2, 1.2, 34);
      else this.player.spawn(this.level.playerStart, Math.PI);
    }
    (window as unknown as { __halyardReady: boolean }).__halyardReady = true;
    this.loop();
    this.tickClock();
  }

  dispose() {
    cancelAnimationFrame(this.raf);
    this.input.dispose();
    this.stack.dispose();
  }

  private bindUI() {
    const q = this.root.querySelector<HTMLSelectElement>("#quality")!;
    const sens = this.root.querySelector<HTMLInputElement>("#sens")!;
    const bob = this.root.querySelector<HTMLInputElement>("#headbob")!;
    q.addEventListener("change", () => {
      this.quality = q.value as QualityPreset;
      this.stack?.setQuality(this.quality);
      this.lights?.setQuality(this.quality, this.scene);
    });
    sens.addEventListener("input", () => {
      if (this.player) this.player.sensMul = parseFloat(sens.value);
    });
    bob.addEventListener("change", () => {
      if (this.player) this.player.headBob = bob.checked;
    });

    const show = (id: string, v: boolean) => this.root.querySelector("#" + id)!.classList.toggle("hidden", !v);

    this.root.querySelector("#btn-raid")!.addEventListener("click", async () => {
      await this.audio.unlock();
      this.audio.play("ui2", { vol: 0.5 });
      show("menu", false);
      show("settings", false);
      this.beginRaid();
    });
    this.root.querySelector("#btn-settings")!.addEventListener("click", () => {
      this.audio.play("ui", { vol: 0.4 });
      show("settings", true);
    });
    this.root.querySelector("#btn-credits")!.addEventListener("click", () => {
      this.audio.play("ui", { vol: 0.4 });
      show("credits", true);
    });
    this.root.querySelector("#btn-settings-close")!.addEventListener("click", () => show("settings", false));
    this.root.querySelector("#btn-credits-close")!.addEventListener("click", () => show("credits", false));
    this.root.querySelector("#btn-resume")!.addEventListener("click", () => this.resume());
    this.root.querySelector("#btn-restart")!.addEventListener("click", () => {
      show("pause", false);
      this.beginRaid();
    });
    this.root.querySelector("#btn-quit")!.addEventListener("click", () => {
      show("pause", false);
      show("hud", false);
      show("menu", true);
      this.mode = "menu";
      this.input.exitLock();
      document.body.classList.remove("cinematic");
    });
    this.root.querySelector("#btn-respawn")!.addEventListener("click", () => {
      show("death", false);
      this.beginRaid();
    });
    this.root.querySelector("#btn-again")!.addEventListener("click", () => {
      show("victory", false);
      this.beginRaid();
    });
  }

  private beginRaid() {
    this.player.spawn(this.level.playerStart, Math.PI);
    this.arsenal.select(0);
    for (const w of this.arsenal.weapons) {
      w.mag = w.def.mag;
      w.res = w.def.reserves;
      w.reloading = false;
    }
    // reset enemies (keep the group in the scene — removing it made ghosts)
    if (!this.director.group.parent) this.scene.add(this.director.group);
    for (let i = 0; i < this.director.enemies.length; i++) {
      const e = this.director.enemies[i];
      const s = this.level.spawns[i];
      e.hp = 100;
      e.state = "patrol";
      e.pos.copy(s.pos);
      e.yaw = s.yaw;
      e.alert = 0;
      e.deadT = 0;
      e.acquire = 1.4;
      e.marker.visible = true;
      e.root.rotation.x = 0;
      e.root.position.set(s.pos.x, 0, s.pos.z);
      e.root.visible = true;
      this.director.eject(e);
      e.root.position.set(e.pos.x, 0, e.pos.z);
    }
    this.director.kills = 0;
    this.mission.start();
    this.warnedContact = false;
    this.director.frozen = false;
    this.mode = "playing";
    this.hud.show(true);
    this.root.querySelector("#menu")!.classList.add("hidden");
    this.root.querySelector("#pause")!.classList.add("hidden");
    this.root.querySelector("#death")!.classList.add("hidden");
    this.root.querySelector("#victory")!.classList.add("hidden");
    document.body.classList.add("cinematic");
    this.input.requestLock();
    this.hud.announce("SHOOT THE RED DIAMONDS — FIRST TARGET IS AHEAD");
    this.audio.play("ui2", { vol: 0.45 });
  }

  private resume() {
    this.mode = "playing";
    this.root.querySelector("#pause")!.classList.add("hidden");
    this.input.requestLock();
  }

  private pause() {
    if (this.mode !== "playing") return;
    this.mode = "paused";
    this.root.querySelector("#pause")!.classList.remove("hidden");
    this.input.exitLock();
  }

  private die(cause: string) {
    this.mode = "dead";
    this.root.querySelector("#death")!.classList.remove("hidden");
    this.root.querySelector("#death-cause")!.textContent = cause;
    this.input.exitLock();
    this.audio.play("hurt", { vol: 0.8 });
  }

  private win() {
    this.mode = "victory";
    const s = this.mission.stats;
    const acc = s.shots ? Math.round((s.hits / s.shots) * 100) : 0;
    this.root.querySelector("#victory-stats")!.textContent =
      `${s.kills} KIA · ${acc}% HIT · ${s.time.toFixed(0)}s`;
    this.root.querySelector("#victory")!.classList.remove("hidden");
    this.input.exitLock();
  }

  private loop = () => {
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(0.05, this.clock.getDelta());
    const time = this.clock.elapsedTime;
    this.tick(dt, time);
    this.input.endFrame();
  };

  private tick(dt: number, time: number) {
    if (this.mode === "menu") {
      this.attract += dt;
      this.arsenal.group.visible = false;
      this.camera.position.set(Math.sin(this.attract * 0.12) * 8 + 4, 3.4, 10 + Math.cos(this.attract * 0.1) * 6);
      this.camera.lookAt(0, 1.4, 28);
      this.lights.update(new THREE.Vector3(0, 0, 24), dt);
      this.weather.update(dt, this.camera.position);
      this.stack.render(dt);
      return;
    }

    if (this.input.pressed("Escape")) {
      if (this.mode === "playing") this.pause();
      else if (this.mode === "paused") this.resume();
    }
    if (this.mode === "paused" || this.mode === "dead" || this.mode === "victory") {
      this.lights.update(this.player.mover.pos, dt);
      this.weather.update(dt, this.player.camera.position);
      this.fx.update(dt, this.camera);
      this.stack.render(dt);
      return;
    }

    if (!this.input.locked && this.mode === "playing") {
      // wait for click to relock
      this.canvas.addEventListener("click", () => this.input.requestLock(), { once: true });
    }

    this.arsenal.group.visible = true;
    if (this.input.pressed("Digit1")) this.arsenal.select(0);
    if (this.input.pressed("Digit2")) this.arsenal.select(1);
    if (this.input.pressed("Digit3")) this.arsenal.select(2);
    if (this.input.pressed("Digit4")) this.arsenal.select(3);
    if (this.input.wheel) this.arsenal.cycle(this.input.wheel > 0 ? 1 : -1);
    if (this.input.pressed("KeyR")) this.arsenal.tryReload(this.audio);
    if (this.input.pressed("KeyG")) this.throwNade();

    this.player.update(dt, this.input, this.level.colliders, this.audio, time);
    this.audio.setListener(this.camera.position.x, this.camera.position.y, this.camera.position.z);

    // wall probe for gun
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    const wall = hitscan(this.camera.position, dir, 0.85, this.level.colliders);
    this.arsenal.setWallPush(wall ? 1 - wall.dist / 0.85 : 0);

    this.arsenal.update(dt, time, this.player, this.input.fire && this.input.locked, this.input.ads && this.input.locked, this.audio, (origin, aim, w) => {
      this.mission.stats.shots += w.def.pellets;
      const spread = THREE.MathUtils.lerp(w.def.spreadHip, w.def.spreadAds, w.ads);
      const move = Math.hypot(this.player.mover.vel.x, this.player.mover.vel.z);
      const air = this.player.mover.grounded ? 1 : 1.7;
      for (let i = 0; i < w.def.pellets; i++) {
        const d = aim.clone();
        d.x += (Math.random() - 0.5) * spread * air * (1 + move * 0.08);
        d.y += (Math.random() - 0.5) * spread * air;
        d.z += (Math.random() - 0.5) * spread * air;
        d.normalize();
        this.fireRound(origin, d, w.def.dmg, w.def.range, true);
      }
    });

    this.director.update(dt, time, this.player, this.audio, (origin, d, e) => {
      const world = hitscan(origin, d, 70, this.level.colliders);
      const playerDist = this.rayPlayer(origin, d);
      if (playerDist !== null && (!world || playerDist < world.dist)) {
        const spawnGrace = performance.now() / 1000 - this.mission.startedAt < 8;
        if (spawnGrace) return;
        const ang = this.player.hurt(4 + Math.random() * 3, e.pos, this.audio);
        this.hud.damageDir(typeof ang === "number" ? ang : 0);
        if (!this.warnedContact) {
          this.warnedContact = true;
          this.hud.announce("CONTACT — RED MARKERS · USE CRATES");
        }
        this.fx.impact(origin.clone().addScaledVector(d, playerDist), d.clone().multiplyScalar(-1), "flesh");
        if (!this.player.alive) this.die(`${e.name.toUpperCase()} · VESPER RIFLEMAN`);
      } else if (world) {
        this.fx.impact(world.point, new THREE.Vector3(0, 1, 0), "world");
      }
    });

    this.updateNades(dt);
    const toast = this.mission.update(dt, this.player, this.director, this.audio, this.input.held("KeyF"));
    if (toast) this.hud.announce(toast);
    if (this.mission.phase === "done" && this.mode === "playing") this.win();

    this.lights.update(this.player.mover.pos, dt);
    this.weather.update(dt, this.player.camera.position);
    this.fx.update(dt, this.camera);
    this.hud.update(dt, this.player, this.arsenal, this.mission, this.level, this.director);
    this.stack.render(dt);
  }

  private fireRound(origin: THREE.Vector3, dir: THREE.Vector3, dmg: number, range: number, fromPlayer: boolean) {
    const world = hitscan(origin, dir, range, this.level.colliders);
    const eh = this.director.hitTest(origin, dir, range);
    const wd = world?.dist ?? range;
    const ed = eh?.dist ?? range;
    if (eh && ed <= wd) {
      if (fromPlayer) {
        this.mission.stats.hits++;
        const mul = eh.part === "head" ? 1.65 : 1;
        const killed = this.director.damage(eh.enemy, dmg * mul, this.player.camera.position);
        this.hud.hit(eh.part === "head");
        this.audio.play(eh.part === "head" ? "head" : "hit", { vol: 0.45 });
        this.fx.impact(eh.point, dir.clone().multiplyScalar(-1), "flesh");
        this.fx.tracer(origin.clone().add(dir.clone().multiplyScalar(0.4)), eh.point);
        if (killed) {
          this.hud.kill(eh.enemy.name);
          this.audio.play("ui", { vol: 0.35 });
        }
      }
      return;
    }
    if (world) {
      this.fx.impact(world.point, new THREE.Vector3(0, 1, 0), "world");
      this.fx.tracer(origin.clone().add(dir.clone().multiplyScalar(0.35)), world.point);
      if (fromPlayer && Math.random() < 0.2) this.audio.play("rico", { vol: 0.18, x: world.point.x, y: world.point.y, z: world.point.z });
    } else {
      this.fx.tracer(origin.clone().add(dir.clone().multiplyScalar(0.35)), origin.clone().addScaledVector(dir, range * 0.6));
    }
  }

  private rayPlayer(origin: THREE.Vector3, dir: THREE.Vector3) {
    const center = this.player.camera.position.clone();
    const oc = origin.clone().sub(center);
    const b = oc.dot(dir);
    const disc = b * b - (oc.lengthSq() - 0.42 * 0.42);
    if (disc < 0) return null;
    const t = -b - Math.sqrt(disc);
    if (t < 0 || t > 70) return null;
    return t;
  }

  private throwNade() {
    if (this.nadeCd > 0 || this.mode !== "playing") return;
    this.nadeCd = 1.4;
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    const mesh = new THREE.Mesh(this.nadeGeo, this.nadeMat);
    mesh.castShadow = true;
    mesh.position.copy(this.camera.position).addScaledVector(dir, 0.6);
    this.scene.add(mesh);
    this.grenades.push({
      pos: mesh.position.clone(),
      vel: dir.multiplyScalar(16).add(new THREE.Vector3(0, 3.5, 0)),
      life: 1.55,
      mesh,
    });
    this.audio.play("whoosh", { vol: 0.4 });
  }

  private updateNades(dt: number) {
    this.nadeCd = Math.max(0, this.nadeCd - dt);
    for (let i = this.grenades.length - 1; i >= 0; i--) {
      const g = this.grenades[i];
      g.vel.y -= 18 * dt;
      g.pos.addScaledVector(g.vel, dt);
      if (g.pos.y < 0.08) {
        g.pos.y = 0.08;
        g.vel.y *= -0.35;
        g.vel.x *= 0.7;
        g.vel.z *= 0.7;
      }
      const hit = hitscan(g.pos.clone().sub(g.vel.clone().setLength(0.1)), g.vel.clone().normalize(), 0.25, this.level.colliders);
      if (hit) {
        g.vel.multiplyScalar(-0.3);
        g.pos.copy(hit.point).add(new THREE.Vector3(0, 0.1, 0));
      }
      g.mesh.position.copy(g.pos);
      g.life -= dt;
      if (g.life <= 0) {
        this.fx.explosion(g.pos);
        this.audio.play("explode", { vol: 0.85, x: g.pos.x, y: g.pos.y, z: g.pos.z });
        for (const e of this.director.enemies) {
          if (e.state === "dead") continue;
          const d = e.pos.distanceTo(g.pos);
          if (d < 6.5) {
            const killed = this.director.damage(e, 140 * (1 - d / 6.5), g.pos);
            if (killed) this.hud.kill(e.name);
          }
        }
        const pd = this.player.mover.pos.distanceTo(g.pos);
        if (pd < 5.5) {
          const ang = this.player.hurt(55 * (1 - pd / 5.5), g.pos, this.audio);
          this.hud.damageDir(typeof ang === "number" ? ang : false);
          if (!this.player.alive) this.die("OWN FRAG · TERMINAL 9");
        }
        this.scene.remove(g.mesh);
        this.grenades.splice(i, 1);
      }
    }
  }

  private tickClock() {
    const el = this.root.querySelector("#clock")!;
    const tick = () => {
      const d = new Date();
      el.textContent = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}:${String(d.getUTCSeconds()).padStart(2, "0")}Z`;
      requestAnimationFrame(tick);
    };
    tick();
  }
}
